/**
 * ContactTranscriptDialog (TASK-208)
 *
 * Modal with a client conversation. The body (header, feed, input, materials)
 * is ContactConversationPanel; the history of its behaviour lives there.
 *
 * KAN-736: the modal is almost as tall as the window, the feed takes all the
 * free height; "Широкий вид" next to the title opens the same conversation on
 * its own page (/clients/<id>/chat) in a new tab.
 */
import { Maximize2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'
import { type SenderAccountLinkAccount } from '@/components/messages/SenderAccountLink'
import { ContactConversationPanel } from '@/components/contact/ContactConversationPanel'
import { contactChatPath } from '@/components/contact/contactChatPath'

interface ContactTranscriptDialogProps {
  /**
   * Estimate of the dialog: manual messages are recorded into it; without a
   * clientId its messages form the transcript.
   */
  contactId: string | null
  /** Human-readable dialog title (client name / display name). */
  title: string
  /**
   * KAN-626: client of the dialog; without it no manual mode and no input.
   * KAN-654: with it the feed is all messages of the client.
   */
  clientId?: string | null
  /** KAN-626: manual mode if the caller knows it (else read from the client). */
  manualMode?: boolean
  /**
   * KAN-636: our Telegram account of the dialog. undefined: taken from the
   * latest message that has one; null: none, the header shows nothing.
   */
  senderAccount?: SenderAccountLinkAccount | null
  open: boolean
  onOpenChange: (open: boolean) => void
}

function WideViewButton({ clientId }: { clientId: string }) {
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      className="h-7 w-7 shrink-0 text-gray-500 hover:text-gray-900"
      aria-label="Широкий вид"
      title="Широкий вид"
      onClick={() => window.open(contactChatPath(clientId), '_blank', 'noopener')}
      data-testid="transcript-wide-view"
    >
      <Maximize2 className="h-4 w-4" aria-hidden="true" />
    </Button>
  )
}

export function ContactTranscriptDialog({
  contactId,
  title,
  clientId = null,
  manualMode,
  senderAccount,
  open,
  onOpenChange,
}: ContactTranscriptDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex h-[90vh] max-h-[calc(100vh-2rem)] max-w-3xl flex-col gap-3 p-4 pt-5 sm:p-6">
        <ContactConversationPanel
          contactId={contactId}
          title={title}
          clientId={clientId}
          manualMode={manualMode}
          senderAccount={senderAccount}
          active={open}
          TitleComponent={DialogTitle}
          onLeave={() => onOpenChange(false)}
          titleAction={(id) => <WideViewButton clientId={id} />}
          headerClassName="pr-6"
        />
      </DialogContent>
    </Dialog>
  )
}
